// Bid Tracker merge: the preview the dialog shows before anyone presses Merge.
//
// POST crm/bids/:id/merge keeps the survivor, fills its blank fields from the
// duplicate, appends the duplicate's notes under a "Merged from" line and
// moves its platforms, documents, hardware, status history and comments across. The
// duplicate row stays, marked merged_into_id, so its history remains truthful.
// The dialog works all of that out from the two rows it already holds, and
// filters the loaded bid list down to candidates.
//
// Pure functions only, run by tests/bid-merge.test.mjs.
import { formatValueGbp, valueBadges, fmtDeadlineWithTime } from './bidValue.js';

export const isBlank = (v) => v == null || v === '';

// The survivor fields a merge fills when they are blank, in the order the
// dialog lists them. value_gbp brings its three qualifiers with it, so they
// are not fills in their own right; notes are appended rather than filled.
export const MERGE_FILL_FIELDS = [
  { key: 'buyer', label: 'Buyer' },
  { key: 'sector', label: 'Sector' },
  { key: 'service_line', label: 'Service line' },
  { key: 'value_gbp', label: 'Value', show: (b) => [formatValueGbp(b.value_gbp), ...valueBadges(b)].join(' ') },
  { key: 'est_value', label: 'Legacy value text' },
  { key: 'owner_id', label: 'Owner', show: (b) => b.owner_name || b.owner || `person ${b.owner_id}` },
  { key: 'deadline', label: 'Deadline', show: (b) => fmtDeadlineWithTime(b.deadline, b.deadline_time) },
  { key: 'deadline_time', label: 'Deadline time' },
  { key: 'key_date', label: 'Other dates (legacy text)' },
  { key: 'outcome', label: 'Stage / score' },
  { key: 'result', label: 'Result' },
  { key: 'result_date', label: 'Result date' },
  { key: 'result_score', label: 'Result score' },
  { key: 'feedback', label: 'Buyer feedback' },
  { key: 'no_bid_reason', label: 'No-bid reason' },
  { key: 'winning_supplier', label: 'Winning supplier' },
  { key: 'winning_price_gbp', label: 'Winning price', show: (b) => formatValueGbp(b.winning_price_gbp) },
  { key: 'last_checked_at', label: 'Portal last checked', show: (b) => fmtDeadlineWithTime(b.last_checked_at) },
  { key: 'promoted_opportunity_id', label: 'Pipeline opportunity link' },
  { key: 'sharepoint_folder_url', label: 'SharePoint folder', show: (b) => b.sharepoint_folder_url },
  { key: 'won_sow_id', label: 'Won SOW link' },
];

/**
 * What the merge will do to `survivor` given `duplicate`:
 * `fills` are the blank survivor fields the duplicate can supply, each with a
 * printable `value`; `platforms` is how many of the duplicate's platform rows
 * move; `notesAppended` says whether the duplicate has notes to append.
 * Documents, history and comments are counted by the dialog from the API,
 * since neither row carries them.
 */
export function mergePreview(survivor, duplicate) {
  const fills = [];
  for (const f of MERGE_FILL_FIELDS) {
    if (!isBlank(survivor[f.key]) || isBlank(duplicate[f.key])) continue;
    // The deadline fill already shows the time when both come across.
    if (f.key === 'deadline_time' && fills.some(x => x.key === 'deadline')) continue;
    fills.push({ key: f.key, label: f.label, value: f.show ? f.show(duplicate) : String(duplicate[f.key]) });
  }
  return {
    fills,
    platforms: Array.isArray(duplicate.platforms) ? duplicate.platforms.length : 0,
    notesAppended: !isBlank(duplicate.notes) && String(duplicate.notes).trim() !== '',
  };
}

/**
 * The bids that could be merged with `bid`: everything except itself and rows
 * already merged away, narrowed by `query` against reference, opportunity and
 * buyer, case-insensitively.
 */
export function filterMergeCandidates(bids, bid, query) {
  const q = String(query || '').trim().toLowerCase();
  return (Array.isArray(bids) ? bids : []).filter(b => {
    if (!b || b.id === bid.id || b.merged_into_id != null) return false;
    if (!q) return true;
    return [b.reference, b.opportunity, b.buyer].some(v => v && String(v).toLowerCase().includes(q));
  });
}
